import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../layouts/DashboardLayout';
import { listUsers } from '../api/users';
import { listSubjects } from '../api/subjects';
import {
  createEducatorAssignment,
  deleteEducatorAssignment,
  listEducatorAssignments,
  updateEducatorAssignment,
} from '../api/educatorAssignments';
import { formatDateTime } from '../utils/format';
import { useSnackbar } from 'notistack';

const EducatorAssignmentsPage = () => {
  const [rows, setRows] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [educators, setEducators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    year: '',
    grade: '',
    subjectId: '',
    educatorId: '',
    isActive: 'all',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState({
    year: '',
    grade: '',
    subjectId: '',
    educatorId: '',
    isActive: true,
    notes: '',
  });
  const { enqueueSnackbar } = useSnackbar();

  const loadLookups = useCallback(async () => {
    try {
      const [subjectData, educatorData] = await Promise.all([
        listSubjects(),
        listUsers({ role: 'educator' }),
      ]);
 setSubjects(Array.isArray(subjectData) ? subjectData : []);
 setEducators(Array.isArray(educatorData) ? educatorData : []);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to load lookup data', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.grade) params.grade = filters.grade;
      if (filters.subjectId) params.subjectId = filters.subjectId;
      if (filters.educatorId) params.educatorId = filters.educatorId;
      if (filters.isActive !== 'all') params.isActive = filters.isActive === 'active';
      const data = await listEducatorAssignments(params);
 setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load educator assignments');
    } finally {
      setLoading(false);
    }
  }, [filters.educatorId, filters.grade, filters.isActive, filters.subjectId, filters.year]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const resetForm = useCallback((row) => {
    if (row) {
      setForm({
        year: row.year || '',
        grade: row.grade || '',
        subjectId: row.subjectId?._id || row.subjectId || '',
        educatorId: row.educatorId?._id || row.educatorId || '',
        isActive: row.isActive !== false,
        notes: row.notes || '',
      });
    } else {
      setForm({
        year: '',
        grade: '',
        subjectId: '',
        educatorId: '',
        isActive: true,
        notes: '',
      });
    }
  }, []);

  const handleOpenCreate = () => {
    setSelectedRow(null);
    resetForm(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setSelectedRow(row);
    resetForm(row);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setDialogLoading(true);
      const payload = {
        educatorId: form.educatorId,
        subjectId: form.subjectId,
        grade: Number(form.grade),
 year: form.year === '' ? null : Number(form.year),
        isActive: Boolean(form.isActive),
        notes: form.notes || null,
      };

      if (selectedRow?._id) {
        await updateEducatorAssignment(selectedRow._id, payload);
        enqueueSnackbar('Assignment updated', { variant: 'success' });
      } else {
        await createEducatorAssignment(payload);
        enqueueSnackbar('Assignment created', { variant: 'success' });
      }
      setDialogOpen(false);
      await loadAssignments();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to save assignment', { variant: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const educatorName = row.educatorId?.name || 'educator';
    const confirmed = window.confirm(`Delete assignment for ${educatorName} This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteEducatorAssignment(row._id);
      enqueueSnackbar('Assignment deleted', { variant: 'success' });
      await loadAssignments();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to delete assignment', { variant: 'error' });
    }
  };
  const subjectOptions = useMemo(() => (
    filters.grade ?
      subjects.filter((s) => Number(s.grade) === Number(filters.grade))
      : subjects
  ), [filters.grade, subjects]);
  const formSubjectOptions = useMemo(() => (
    form.grade ?
      subjects.filter((s) => Number(s.grade) === Number(form.grade))
      : subjects
  ), [form.grade, subjects]);

  const columns = useMemo(() => ([
    {
      field: 'grade',
      headerName: 'Grade',
      minWidth: 100,
    },
    {
      field: 'subject',
      headerName: 'Subject',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params?.row?.subjectId?.name || '-',
    },
    {
      field: 'educator',
      headerName: 'Educator',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        const educator = params?.row?.educatorId;
        if (!educator) return '-';
        return `${educator.name || ''} ${educator.surname || ''}`.trim();
      },
    },
    {
      field: 'year',
      headerName: 'Year',
      minWidth: 110,
      valueGetter: (params) => params?.row?.year || 'All',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params?.row?.isActive ? 'Active' : 'Inactive'}
          color={params?.row?.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      minWidth: 190,
      valueGetter: (params) => (params?.row?.updatedAt ? formatDateTime(params.row.updatedAt) : '-'),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [handleDelete, handleOpenEdit]);

  return (
    <DashboardLayout
      title="Assign Educators"
      actions={(
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Year"
            type="number"
            value={filters.year}
            onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Grade"
            type="number"
            value={filters.grade}
            onChange={(event) => setFilters((prev) => ({ ...prev, grade: event.target.value }))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            select
            size="small"
            label="Subject"
            value={filters.subjectId}
            onChange={(event) => setFilters((prev) => ({ ...prev, subjectId: event.target.value }))}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {subjectOptions.map((subject) => (
              <MenuItem key={subject._id} value={subject._id}>
                {subject.name} (G{subject.grade})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Educator"
            value={filters.educatorId}
            onChange={(event) => setFilters((prev) => ({ ...prev, educatorId: event.target.value }))}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {educators.map((educator) => (
              <MenuItem key={educator._id} value={educator._id}>
                {educator.name} {educator.surname}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={filters.isActive}
            onChange={(event) => setFilters((prev) => ({ ...prev, isActive: event.target.value }))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAssignments}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate}>
            Add Assignment
          </Button>
        </Stack>
      )}
    >
      <Box>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Educator -> Grade + Subject"
              subheader="Mappings are global by default and automatically apply to new sections."
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box sx={{ height: 520 }}>
                <DataGrid
                  rows={rows}
                  getRowId={(row) => row._id}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '& .MuiDataGrid-virtualScroller': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-row': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-cell': { color: 'text.primary' },
                    '& .MuiDataGrid-cellContent': { color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: 'text.primary' },
                    '& .MuiDataGrid-footerContainer': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-overlay': { color: 'text.secondary' },
                  }}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
 <DialogTitle>{selectedRow ? 'Edit Assignment' : 'Add Assignment'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Year (optional)"
                type="number"
                value={form.year}
                onChange={(event) => setForm((prev) => ({ ...prev, year: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Grade"
                type="number"
                value={form.grade}
                onChange={(event) => setForm((prev) => ({ ...prev, grade: event.target.value }))}
                fullWidth
                required
              />
            </Stack>
            <TextField
              select
              label="Subject"
              value={form.subjectId}
              onChange={(event) => {
                const subjectId = event.target.value;
                const subject = subjects.find((item) => item._id === subjectId);
                setForm((prev) => ({
                  ...prev,
                  subjectId,
                  grade: subject?.grade || prev.grade,
                }));
              }}
              fullWidth
              required
            >
              {formSubjectOptions.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>
                  {subject.name} (G{subject.grade})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Educator"
              value={form.educatorId}
              onChange={(event) => setForm((prev) => ({ ...prev, educatorId: event.target.value }))}
              fullWidth
              required
            >
              {educators.map((educator) => (
                <MenuItem key={educator._id} value={educator._id}>
                  {educator.name} {educator.surname}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={dialogLoading}>
 {dialogLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default EducatorAssignmentsPage;
